/*
 * Guide capture — email in, download out, no account.
 *
 * Every [data-guide-capture] root is one guide's form. On submit the address goes
 * to that guide's ActiveCampaign form and the download opens on the page at once.
 * ActiveCampaign owns everything after that: the guide email and the workflow.
 *
 * Why the ActiveCampaign hosted-form endpoint, loaded as JSONP:
 *   - It needs no API key, so nothing secret reaches the browser. Form IDs are public.
 *   - It is the endpoint ActiveCampaign's own embed code uses, so a form's
 *     "subscribes to form" automation and its tag actions fire exactly as they do
 *     for an embedded form.
 *   - It answers cross-origin by calling a global function, which is why
 *     ActiveCampaign submission itself lives in assets/la-ac.js.
 *
 * Failure policy — the visitor always gets the guide unless ActiveCampaign says
 * the address itself is wrong:
 *   - _show_error (bad address, form misconfigured): show the message, keep the form.
 *   - Script blocked or no answer in time (ad blockers, network): open the download
 *     anyway. A missed lead costs less than a visitor who asked for a guide and
 *     got nothing.
 *
 * This is a lead-magnet form, not access control. The PDF address is in the page
 * so it can open without a round trip; Shopify file URLs are public regardless.
 */
(function () {
  'use strict';

  var STORE_KEY = 'la:guides:unlocked';
  function readUnlocked() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; }
  }
  function markUnlocked(handle) {
    try {
      var all = readUnlocked();
      all[handle] = Date.now();
      localStorage.setItem(STORE_KEY, JSON.stringify(all));
    } catch (e) { /* private mode: the download still opened for this visit */ }
  }

  function sendToAC(account, formId, email) {
    // Shared with the promo popup: see assets/la-ac.js for why it must be one file.
    return window.LAActiveCampaign
      ? window.LAActiveCampaign.subscribe(account, formId, email)
      : Promise.reject({ fromAC: false });
  }

  function track(handle, title) {
    var detail = { guide: handle, guide_title: title };
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish('guide_download', detail);
      }
    } catch (e) { /* analytics must never block the download */ }
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: 'guide_download' }, detail));
    }
  }

  function showReady(root, moveFocus) {
    var form = root.querySelector('[data-gc-form]');
    var ready = root.querySelector('[data-gc-ready]');
    if (!ready) return;
    if (form) form.hidden = true;
    ready.hidden = false;
    root.setAttribute('data-state', 'ready');
    if (moveFocus) {
      var heading = ready.querySelector('[data-gc-ready-heading]');
      if (heading) heading.focus();
    }
  }

  function init(root) {
    if (root.dataset.gcInit) return;
    root.dataset.gcInit = '1';

    var handle = root.getAttribute('data-guide');
    var title = root.getAttribute('data-guide-title') || handle;
    var account = (root.getAttribute('data-ac-account') || '').trim();
    var formId = (root.getAttribute('data-ac-form') || '').trim();
    var form = root.querySelector('[data-gc-form]');
    var input = root.querySelector('input[type="email"]');
    var button = root.querySelector('[data-gc-submit]');
    var error = root.querySelector('[data-gc-error]');

    // Returning visitor on this device: straight to the download, no form.
    if (readUnlocked()[handle]) { showReady(root, false); return; }
    if (!form || !input || !button) return;

    function setError(message) {
      if (!error) return;
      error.textContent = message || '';
      error.hidden = !message;
      if (message) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    }

    function unlock() {
      markUnlocked(handle);
      track(handle, title);
      showReady(root, true);
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var email = input.value.trim();
      if (!input.checkValidity() || !email) {
        setError(root.getAttribute('data-msg-invalid') || 'Enter a valid email address.');
        input.focus();
        return;
      }
      setError('');

      if (!account || !formId) {
        console.warn('[la] guide capture: no ActiveCampaign account or form ID for "' + handle + '". Download opened, signup not recorded.');
        unlock();
        return;
      }

      var label = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = root.getAttribute('data-msg-sending') || 'Sending…';

      sendToAC(account, formId, email)
        .then(unlock)
        .catch(function (err) {
          if (err && err.fromAC) {
            setError(err.message ? String(err.message).replace(/<[^>]*>/g, '') : (root.getAttribute('data-msg-invalid') || 'Enter a valid email address.'));
            input.focus();
          } else {
            // Blocked or unreachable: hand the address to Shopify tagged for its
            // form (see saveToShopify in assets/la-ac.js). The download opens
            // either way — a visitor who asked for a guide never leaves without it.
            var fallback = window.LAActiveCampaign && window.LAActiveCampaign.saveToShopify
              ? window.LAActiveCampaign.saveToShopify(email, formId, ['guide-download'])
              : Promise.resolve(false);
            return fallback.then(function (saved) {
              if (!saved) console.warn('[la] guide capture: ActiveCampaign unreachable and Shopify fallback refused. Download opened; signup not recorded.');
              unlock();
            });
          }
        })
        .finally(function () {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = label;
        });
    });
  }

  // --- Guide picker dialog (Guide Central grid) --------------------------------
  // Each card's link goes to the guide's own page, so the grid still works with no
  // JavaScript. With it, the link opens the guide's form in a dialog instead.
  function initDialog(section) {
    if (section.dataset.gcDialogInit) return;
    section.dataset.gcDialogInit = '1';
    var dialog = section.querySelector('[data-gc-dialog]');
    var body = dialog && dialog.querySelector('[data-gc-dialog-body]');
    if (!dialog || !body || typeof dialog.showModal !== 'function') return;

    section.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-gc-open]');
      if (!trigger || !section.contains(trigger)) return;
      var tpl = section.querySelector('template[data-gc-template="' + trigger.getAttribute('data-gc-open') + '"]');
      if (!tpl) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return; // new-tab intent
      event.preventDefault();
      body.replaceChildren(tpl.content.cloneNode(true));
      body.querySelectorAll('[data-guide-capture]').forEach(init);
      dialog.showModal();
      var first = body.querySelector('[data-gc-ready]:not([hidden]) a, input[type="email"]');
      if (first) first.focus();
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.closest('[data-gc-close]')) dialog.close();
    });
    // The close event is queued, so it can land after a fast reopen. Only clear a
    // dialog that is still closed, or the new guide's form would be wiped.
    dialog.addEventListener('close', function () { if (!dialog.open) body.replaceChildren(); });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-guide-capture]').forEach(init);
    (scope || document).querySelectorAll('[data-gc-picker]').forEach(initDialog);
  }

  boot();
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})();
